/**
 * Design Tokens for ERP System
 * Provides type-safe access to design system values
 * Used by reusable components for consistent styling
 */

/**
 * Status color mappings for ERP document statuses
 * Each status has background, text, and border colors
 */
export const statusColors = {
  // Document workflow statuses
  DRAFT: { bg: '#fafafa', text: '#8c8c8c', border: '#d9d9d9', antColor: 'default' },
  SUBMITTED: { bg: '#e6f4ff', text: '#1677ff', border: '#91caff', antColor: 'processing' },
  APPROVED: { bg: '#f6ffed', text: '#52c41a', border: '#b7eb8f', antColor: 'success' },
  CONFIRMED: { bg: '#e6f4ff', text: '#1677ff', border: '#91caff', antColor: 'blue' },
  REJECTED: { bg: '#fff2f0', text: '#ff4d4f', border: '#ffccc7', antColor: 'error' },
  CANCELLED: { bg: '#fafafa', text: '#8c8c8c', border: '#d9d9d9', antColor: 'default' },
  COMPLETED: { bg: '#f6ffed', text: '#389e0d', border: '#95de64', antColor: 'success' },
  PROCESSING: { bg: '#e6f4ff', text: '#1890ff', border: '#91caff', antColor: 'processing' },
  OPEN: { bg: '#e6f4ff', text: '#1677ff', border: '#91caff', antColor: 'processing' },

  // Shipping statuses
  RESERVED: { bg: '#e6f4ff', text: '#1677ff', border: '#91caff', antColor: 'processing' },
  PICKED: { bg: '#e6fffb', text: '#13c2c2', border: '#87e8de', antColor: 'cyan' },
  SHIPPED: { bg: '#e6fffb', text: '#13c2c2', border: '#87e8de', antColor: 'cyan' },
  DELIVERED: { bg: '#f6ffed', text: '#52c41a', border: '#b7eb8f', antColor: 'success' },
  PARTIALLY_SHIPPED: { bg: '#fffbe6', text: '#d48806', border: '#ffe58f', antColor: 'warning' },

  // Inventory statuses
  RECEIVED: { bg: '#f6ffed', text: '#52c41a', border: '#b7eb8f', antColor: 'success' },
  PARTIALLY_RECEIVED: { bg: '#fffbe6', text: '#d48806', border: '#ffe58f', antColor: 'warning' },

  // General statuses
  CLOSED: { bg: '#f5f5f5', text: '#595959', border: '#bfbfbf', antColor: 'default' },
  ACTIVE: { bg: '#f6ffed', text: '#52c41a', border: '#b7eb8f', antColor: 'success' },
  INACTIVE: { bg: '#fafafa', text: '#8c8c8c', border: '#d9d9d9', antColor: 'default' },

  // Finance statuses
  POSTED: { bg: '#f6ffed', text: '#52c41a', border: '#b7eb8f', antColor: 'success' },
  VOIDED: { bg: '#fff2f0', text: '#ff4d4f', border: '#ffccc7', antColor: 'error' },
  PAID: { bg: '#f6ffed', text: '#52c41a', border: '#b7eb8f', antColor: 'success' },
  PARTIAL: { bg: '#fffbe6', text: '#d48806', border: '#ffe58f', antColor: 'warning' },
  OVERDUE: { bg: '#fff2f0', text: '#ff4d4f', border: '#ffccc7', antColor: 'error' },

  // Production statuses
  IN_PROGRESS: { bg: '#e6f4ff', text: '#1890ff', border: '#91caff', antColor: 'processing' },
  RELEASED: { bg: '#e6f4ff', text: '#1677ff', border: '#91caff', antColor: 'blue' },
  PENDING: { bg: '#fffbe6', text: '#d48806', border: '#ffe58f', antColor: 'warning' },

  // Quotation statuses
  SENT: { bg: '#e6f4ff', text: '#1677ff', border: '#91caff', antColor: 'blue' },
  ACCEPTED: { bg: '#f6ffed', text: '#52c41a', border: '#b7eb8f', antColor: 'success' },
  EXPIRED: { bg: '#fafafa', text: '#8c8c8c', border: '#d9d9d9', antColor: 'default' },
  CONVERTED: { bg: '#f6ffed', text: '#389e0d', border: '#95de64', antColor: 'success' },
} as const;

/**
 * Aging bucket colors for AP/AR reports
 */
export const agingColors = {
  CURRENT: { color: '#52c41a', label: 'Current' },
  DAYS_1_TO_30: { color: '#faad14', label: '1-30 Days' },
  DAYS_31_TO_60: { color: '#fa8c16', label: '31-60 Days' },
  DAYS_61_TO_90: { color: '#fa541c', label: '61-90 Days' },
  OVER_90: { color: '#ff4d4f', label: 'Over 90 Days' },
} as const;

/**
 * Primary color palette
 */
export const primaryColors = {
  DEFAULT: '#1677ff',
  hover: '#4096ff',
  active: '#0958d9',
  light: '#e6f4ff',
  dark: '#003eb3',
} as const;

/**
 * Semantic colors
 */
export const semanticColors = {
  success: { DEFAULT: '#52c41a', light: '#f6ffed', dark: '#389e0d' },
  warning: { DEFAULT: '#faad14', light: '#fffbe6', dark: '#d48806' },
  error: { DEFAULT: '#ff4d4f', light: '#fff2f0', dark: '#cf1322' },
  info: { DEFAULT: '#1890ff', light: '#e6f7ff', dark: '#096dd9' },
} as const;

/**
 * Neutral color tokens for text and borders
 */
export const neutralColors = {
  textSecondary: '#8c8c8c',
  border: '#f0f0f0',
  textTertiary: '#bfbfbf',
  bgGray: '#fafafa',
} as const;

/**
 * Statistic color tokens (Ant Design 5 compatible)
 */
export const statisticColors = {
  primary: '#1677ff',
  success: '#52c41a',
  warning: '#faad14',
  info: '#13c2c2',
} as const;

/**
 * Spacing values (in pixels)
 */
export const spacing = {
  page: 24,
  card: 16,
  formGap: 16,
  tableGap: 8,
  xs: 4,
  sm: 8,
  md: 16,
  lg: 24,
  xl: 32,
} as const;

/**
 * Responsive breakpoints (in pixels)
 */
export const breakpoints = {
  sm: 640,
  md: 768,
  lg: 1024,
  xl: 1280,
  '2xl': 1536,
} as const;

/**
 * Typography classes (Tailwind)
 */
export const typography = {
  h1: 'text-3xl font-bold',
  h2: 'text-2xl font-semibold',
  h3: 'text-xl font-medium',
  h4: 'text-lg font-medium',
  body: 'text-base',
  small: 'text-sm',
  caption: 'text-xs text-gray-500',
} as const;

/**
 * Get Ant Design Tag color for a status
 * @param status - The status string
 * @returns Ant Design color name
 */
export function getStatusTagColor(status: string): string {
  // Handle null, undefined, empty string, and non-string values
  if (!status || typeof status !== 'string') {
    return 'default';
  }
  const normalizedStatus = status.toUpperCase().replace(/ /g, '_');
  const statusConfig = statusColors[normalizedStatus as keyof typeof statusColors];
  return statusConfig?.antColor || 'default';
}

/**
 * Get status display text (converts underscore to space)
 * @param status - The status string
 * @returns Formatted display text
 */
export function formatStatusText(status: string): string {
  // Handle null, undefined, empty string, and non-string values
  if (!status || typeof status !== 'string') {
    return '-';
  }
  return status.replace(/_/g, ' ');
}

/**
 * Export type for status keys
 *
 * @example
 * // Type-safe status access
 * const status: StatusKey = 'CONFIRMED';
 * const colors = statusColors[status]; // { bg, text, border, antColor }
 */
export type StatusKey = keyof typeof statusColors;

/**
 * Export type for aging bucket keys
 *
 * @example
 * // Type-safe aging bucket access
 * const bucket: AgingBucketKey = 'DAYS_1_TO_30';
 * const config = agingColors[bucket]; // { color, label }
 */
export type AgingBucketKey = keyof typeof agingColors;

/**
 * Format a number as currency with thousand separators
 * @param value - The numeric value
 * @returns Formatted string like "$123,456.78"
 */
export function formatCurrency(value: number): string {
  return `$${value.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

/**
 * Format a date string consistently
 * @param date - ISO date string or Date object
 * @returns Formatted date string like "2026-02-09"
 */
export function formatDate(date: string | Date): string {
  const d = typeof date === 'string' ? new Date(date) : date;
  return d.toLocaleDateString('en-US', { year: 'numeric', month: '2-digit', day: '2-digit' });
}
